import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Search,
  Grid,
  Heart,
  Coffee,
  Car,
  Laptop,
  BookOpen,
  Film,
  ShoppingBag,
  Plane,
  Tag,
  Award,
  RefreshCw,
  X,
} from 'lucide-react';
import { ComingSoonBanner } from '../ComingSoonBanner';
import SearchBar from './components/SearchBar';
import CategoryStrip from './components/CategoryStrip';
import PerkCard from './components/PerkCard';
import FlashDealsSection from './components/FlashDealsSection';
import FilterControls from './components/FilterControls';
import PerkDetailModal from './components/PerkDetailModal';

interface Perk {
  id: string;
  title: string;
  merchant: string;
  merchantLogo: string | null;
  merchantLogoAlt: string;
  category: string;
  discount: number;
  discountType: string;
  originalPrice: number | null;
  discountedPrice: number | null;
  currency: string;
  expiryDate: string;
  location: string;
  description: string;
  terms: string;
  isFlashDeal: boolean;
  flashDealEndsAt?: string;
  availableQuantity?: number;
  isEmployerSponsored: boolean;
  popularity: number;
  imageUrl: string | null;
  imageAlt: string;
}

interface Category {
  id: string;
  name: string;
  icon: string;
}

interface Filters {
  priceRange: string;
  location: string;
  sortBy: string;
}

const MarketplaceHub = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedMerchant, setSelectedMerchant] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>({
    priceRange: '',
    location: '',
    sortBy: 'popularity'
  });
  const [selectedPerk, setSelectedPerk] = useState<Perk | null>(null);
  const [wishlist, setWishlist] = useState<string[]>([]);

  // Categories data
  const categories: Category[] = [
    { id: 'all', name: 'All', icon: 'Grid' },
    { id: 'wellness', name: 'Wellness', icon: 'Heart' },
    { id: 'food', name: 'Food & Dining', icon: 'Coffee' },
    { id: 'transport', name: 'Transport', icon: 'Car' },
    { id: 'electronics', name: 'Electronics', icon: 'Laptop' },
    { id: 'education', name: 'Education', icon: 'BookOpen' },
    { id: 'entertainment', name: 'Entertainment', icon: 'Film' },
    { id: 'shopping', name: 'Shopping', icon: 'ShoppingBag' },
    { id: 'travel', name: 'Travel', icon: 'Plane' }
  ];

  // Mock perks data
  const mockPerks: Perk[] = [
    {
      id: 'PERK001',
      title: '25% Off Gym Membership',
      merchant: 'FitZone Wellness',
      merchantLogo: null,
      merchantLogoAlt: 'FitZone Wellness gym logo',
      category: 'wellness',
      discount: 25,
      discountType: 'percentage',
      originalPrice: 400,
      discountedPrice: 300,
      currency: 'AED',
      expiryDate: '2025-12-31',
      location: 'Dubai',
      description: 'Get 25% off on annual gym membership. Access to all facilities including pool, spa, and group classes.',
      terms: 'Valid for new memberships only. Cannot be combined with other offers.',
      isFlashDeal: false,
      isEmployerSponsored: true,
      popularity: 95,
      imageUrl: null,
      imageAlt: 'Modern gym facility with state-of-the-art equipment and bright lighting'
    },
    {
      id: 'PERK002',
      title: '50% Off All Menu Items',
      merchant: 'Taste of Arabia',
      merchantLogo: null,
      merchantLogoAlt: 'Taste of Arabia restaurant logo',
      category: 'food',
      discount: 50,
      discountType: 'percentage',
      originalPrice: null,
      discountedPrice: null,
      currency: 'AED',
      expiryDate: '2025-11-30',
      location: 'Dubai Marina',
      description: 'Enjoy authentic Middle Eastern cuisine with 50% discount on all menu items.',
      terms: 'Valid Monday-Thursday only. Maximum discount AED 200 per visit.',
      isFlashDeal: true,
      flashDealEndsAt: '2025-11-25T23:59:59',
      availableQuantity: 45,
      isEmployerSponsored: false,
      popularity: 88,
      imageUrl: null,
      imageAlt: 'Traditional Middle Eastern dishes displayed on elegant dining table'
    },
    {
      id: 'PERK003',
      title: 'First Month of Rides On Us',
      merchant: 'RideShare Plus',
      merchantLogo: null,
      merchantLogoAlt: 'RideShare Plus transportation service logo',
      category: 'transport',
      discount: 100,
      discountType: 'percentage',
      originalPrice: 150,
      discountedPrice: 0,
      currency: 'AED',
      expiryDate: '2026-01-31',
      location: 'UAE Wide',
      description: 'Get one month of unlimited rides at no charge. No hidden fees.',
      terms: 'Valid for trips up to AED 150 total value. New users only.',
      isFlashDeal: false,
      isEmployerSponsored: true,
      popularity: 92,
      imageUrl: null,
      imageAlt: 'Modern rideshare car with professional driver in clean interior'
    },
    {
      id: 'PERK004',
      title: '30% Off Latest Electronics',
      merchant: 'TechHub Store',
      merchantLogo: null,
      merchantLogoAlt: 'TechHub Store electronics retailer logo',
      category: 'electronics',
      discount: 30,
      discountType: 'percentage',
      originalPrice: null,
      discountedPrice: null,
      currency: 'AED',
      expiryDate: '2025-12-15',
      location: 'Dubai Mall',
      description: '30% discount on smartphones, laptops, and accessories.',
      terms: 'Excludes Apple products. Subject to stock availability.',
      isFlashDeal: true,
      flashDealEndsAt: '2025-11-23T18:00:00',
      availableQuantity: 120,
      isEmployerSponsored: false,
      popularity: 85,
      imageUrl: null,
      imageAlt: 'Display of modern electronic devices including laptops and smartphones'
    },
    {
      id: 'PERK005',
      title: 'Sponsored Online Course Access',
      merchant: 'SkillUp Academy',
      merchantLogo: null,
      merchantLogoAlt: 'SkillUp Academy online learning platform logo',
      category: 'education',
      discount: 100,
      discountType: 'percentage',
      originalPrice: 500,
      discountedPrice: 0,
      currency: 'AED',
      expiryDate: '2026-06-30',
      location: 'Online',
      description: 'Access to premium courses in technology, business, and design.',
      terms: 'Company-sponsored benefit. Limited to 3 courses per employee per year.',
      isFlashDeal: false,
      isEmployerSponsored: true,
      popularity: 78,
      imageUrl: null,
      imageAlt: 'Person learning on laptop with educational interface visible on screen'
    },
    {
      id: 'PERK006',
      title: '2-for-1 Movie Tickets',
      merchant: 'Cinema Plus',
      merchantLogo: null,
      merchantLogoAlt: 'Cinema Plus movie theater logo',
      category: 'entertainment',
      discount: 50,
      discountType: 'percentage',
      originalPrice: 70,
      discountedPrice: 35,
      currency: 'AED',
      expiryDate: '2025-12-31',
      location: 'All UAE Locations',
      description: 'Buy one movie ticket and get a second one on us. Valid for all shows.',
      terms: 'Not valid on opening weekend of new releases. Subject to seat availability.',
      isFlashDeal: false,
      isEmployerSponsored: false,
      popularity: 90,
      imageUrl: null,
      imageAlt: 'Modern movie theater interior with comfortable seating and large screen'
    },
    {
      id: 'PERK007',
      title: '40% Off Fashion & Apparel',
      merchant: 'Style Avenue',
      merchantLogo: null,
      merchantLogoAlt: 'Style Avenue fashion retailer logo',
      category: 'shopping',
      discount: 40,
      discountType: 'percentage',
      originalPrice: null,
      discountedPrice: null,
      currency: 'AED',
      expiryDate: '2025-11-28',
      location: 'Dubai',
      description: 'Exclusive discount on premium fashion brands and accessories.',
      terms: 'Cannot be combined with ongoing sales. Minimum purchase AED 200.',
      isFlashDeal: true,
      flashDealEndsAt: '2025-11-24T20:00:00',
      availableQuantity: 200,
      isEmployerSponsored: true,
      popularity: 82,
      imageUrl: null,
      imageAlt: 'Stylish clothing display in modern fashion boutique'
    },
    {
      id: 'PERK008',
      title: '20% Off International Flights',
      merchant: 'SkyWings Travel',
      merchantLogo: null,
      merchantLogoAlt: 'SkyWings Travel booking agency logo',
      category: 'travel',
      discount: 20,
      discountType: 'percentage',
      originalPrice: null,
      discountedPrice: null,
      currency: 'AED',
      expiryDate: '2026-03-31',
      location: 'Worldwide',
      description: 'Save on your next international trip with 20% off flight bookings.',
      terms: 'Valid on economy and business class. Blackout dates apply during peak season.',
      isFlashDeal: false,
      isEmployerSponsored: false,
      popularity: 87,
      imageUrl: null,
      imageAlt: 'Airplane flying over scenic clouds during sunset'
    }
  ];

  // Filter perks based on category, merchant, search, and filters
  const filteredPerks = useMemo(() => {
    let perks = [...mockPerks];

    // Category filter
    if (selectedCategory !== 'All') {
      perks = perks.filter(perk => perk.category === selectedCategory.toLowerCase());
    }

    // Merchant filter
    if (selectedMerchant) {
      perks = perks.filter(perk => perk.merchant === selectedMerchant);
    }

    // Search filter
    if (searchTerm) {
      perks = perks.filter(perk =>
        perk.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        perk.merchant.toLowerCase().includes(searchTerm.toLowerCase()) ||
        perk.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Location filter
    if (filters.location) {
      perks = perks.filter(perk => perk.location === filters.location);
    }

    // Price range filter
    if (filters.priceRange) {
      const [min, max] = filters.priceRange.split('-').map(Number);
      perks = perks.filter(perk => {
        const price = perk.discountedPrice || perk.originalPrice || 0;
        return price >= min && price <= max;
      });
    }

    // Sort
    if (filters.sortBy === 'popularity') {
      perks = perks.sort((a, b) => b.popularity - a.popularity);
    } else if (filters.sortBy === 'discount') {
      perks = perks.sort((a, b) => b.discount - a.discount);
    } else if (filters.sortBy === 'expiry') {
      perks = perks.sort((a, b) => new Date(a.expiryDate).getTime() - new Date(b.expiryDate).getTime());
    }

    return perks;
  }, [selectedCategory, selectedMerchant, searchTerm, filters]);

  // Flash deals
  const flashDeals = mockPerks.filter(perk => perk.isFlashDeal);

  // Employer sponsored perks
  const sponsoredPerks = mockPerks.filter(perk => perk.isEmployerSponsored);

  const handleToggleWishlist = (perkId: string) => {
    setWishlist(prev =>
      prev.includes(perkId)
        ? prev.filter(id => id !== perkId)
        : [...prev, perkId]
    );
  };

  const handleMerchantClick = (merchantName: string) => {
    setSelectedMerchant(merchantName);
    setSelectedCategory('All');
  };

  const handleClearMerchantFilter = () => {
    setSelectedMerchant(null);
  };

  const locations = [...new Set(mockPerks.map(perk => perk.location))];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ComingSoonBanner
          icon={<ShoppingBag className="w-6 h-6" />}
          title="Marketplace Hub"
          description="Employee perks and benefits marketplace is currently in development. This is a preview of the upcoming feature."
          gradientFrom="from-amber-500"
          gradientTo="to-yellow-500"
        />

        {/* Header */}
        <div className="mb-8 mt-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {selectedMerchant ? `${selectedMerchant} Perks` : t('Marketplace Hub')}
              </h1>
              <p className="text-gray-600 mt-2">
                {selectedMerchant
                  ? `Discover exclusive offers from ${selectedMerchant}`
                  : t('Discover exclusive employee perks and benefits')
                }
              </p>
            </div>
            {selectedMerchant && (
              <button
                onClick={handleClearMerchantFilter}
                className="flex items-center px-4 py-2 text-sm text-gray-700 bg-card border border-border rounded-lg hover:bg-gray-50 transition-colors duration-200"
              >
                <X className="w-4 h-4 mr-2" />
                {t('View All Perks')}
              </button>
            )}
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <SearchBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
          />
        </div>

        {/* Category Strip */}
        {!selectedMerchant && (
          <div className="mb-6">
            <CategoryStrip
              categories={categories}
              selectedCategory={selectedCategory}
              onCategorySelect={setSelectedCategory}
            />
          </div>
        )}

        {/* Filter Controls */}
        <div className="mb-6">
          <FilterControls
            filters={filters}
            onFiltersChange={setFilters}
            locations={locations}
          />
        </div>

        {/* Flash Deals Section */}
        {!selectedMerchant && flashDeals.length > 0 && (
          <div className="mb-8">
            <FlashDealsSection
              deals={flashDeals}
              onDealClick={(deal: Perk) => setSelectedPerk(deal)}
              onMerchantClick={handleMerchantClick}
              wishlist={wishlist}
              onToggleWishlist={handleToggleWishlist}
            />
          </div>
        )}

        {/* Employer Sponsored Section */}
        {!selectedMerchant && sponsoredPerks.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Award className="w-6 h-6 text-primary mr-2" />
                <h2 className="text-xl font-semibold text-gray-900">{t('Employer-Sponsored Perks')}</h2>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {sponsoredPerks.slice(0, 4).map((perk) => (
                <PerkCard
                  key={perk.id}
                  perk={perk}
                  onPerkClick={() => setSelectedPerk(perk)}
                  onMerchantClick={() => handleMerchantClick(perk.merchant)}
                  isWishlisted={wishlist.includes(perk.id)}
                  onToggleWishlist={() => handleToggleWishlist(perk.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Main Perks Grid */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              {selectedMerchant ? t('All Offers') : selectedCategory === 'All' ? t('All Perks') : `${selectedCategory} ${t('Perks')}`}
            </h2>
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <Tag className="w-4 h-4" />
              <span>{filteredPerks.length} {t('offer')}{filteredPerks.length !== 1 ? 's' : ''} {t('available')}</span>
            </div>
          </div>

          {filteredPerks.length === 0 ? (
            <div className="bg-card rounded-lg shadow-sm border border-border p-12 text-center">
              <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('No perks found')}</h3>
              <p className="text-gray-500 mb-6">
                {t('Try adjusting your search or filters to find what you\'re looking for.')}
              </p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilters({ priceRange: '', location: '', sortBy: 'popularity' });
                  setSelectedCategory('All');
                  setSelectedMerchant(null);
                }}
                className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors duration-200"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {t('Reset Filters')}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredPerks.map((perk) => (
                <PerkCard
                  key={perk.id}
                  perk={perk}
                  onPerkClick={() => setSelectedPerk(perk)}
                  onMerchantClick={() => handleMerchantClick(perk.merchant)}
                  isWishlisted={wishlist.includes(perk.id)}
                  onToggleWishlist={() => handleToggleWishlist(perk.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Perk Detail Modal */}
      {selectedPerk && (
        <PerkDetailModal
          perk={selectedPerk}
          isOpen={!!selectedPerk}
          onClose={() => setSelectedPerk(null)}
          isWishlisted={wishlist.includes(selectedPerk.id)}
          onToggleWishlist={() => handleToggleWishlist(selectedPerk.id)}
        />
      )}
    </div>
  );
};

export default MarketplaceHub;
